import { useCallback, useEffect, useState } from 'react';

import { FilterMatchMode } from 'primereact/api';

import { DROPDOWN_FILTER_DELAY } from '../../../constants';
import { useDebounce } from '../../../hooks';
import { TimelineScale, TreeNode } from '../../../types/interfaces';
import { SortConfig } from '../types';

type FilterField = 'name' | 'personnelNumber';

export type CalendarAssignmentsFiltersState = {
  name: { value: string; matchMode: FilterMatchMode };
  personnelNumber: { value: string; matchMode: FilterMatchMode };
};

const DEFAULT_FILTERS: CalendarAssignmentsFiltersState = {
  name: { value: '', matchMode: FilterMatchMode.CONTAINS },
  personnelNumber: { value: '', matchMode: FilterMatchMode.CONTAINS }
};

export interface UseCalendarAssignmentsTableControlsProps {
  toast: React.RefObject<any>;

  sortConfig: SortConfig;
  setSortConfig: React.Dispatch<React.SetStateAction<SortConfig>>;

  selectedDateFilter: Date | null;
  setSelectedDateFilter: React.Dispatch<React.SetStateAction<Date | null>>;
  setTimelineScale: React.Dispatch<React.SetStateAction<TimelineScale>>;

  persistDepartmentFilter: (selection: TreeNode | null) => void;
  setSelectedDepartmentFilter: React.Dispatch<
    React.SetStateAction<TreeNode | null>
  >;
}

export function useCalendarAssignmentsTableControls(
  props: UseCalendarAssignmentsTableControlsProps
) {
  const {
    toast,
    sortConfig,
    setSortConfig,
    selectedDateFilter,
    setSelectedDateFilter,
    setTimelineScale,
    persistDepartmentFilter,
    setSelectedDepartmentFilter
  } = props;

  const [filters, setFilters] =
    useState<CalendarAssignmentsFiltersState>(DEFAULT_FILTERS);
  const [debouncedFilters, setDebouncedFilters] =
    useState<CalendarAssignmentsFiltersState>(DEFAULT_FILTERS);

  const { debouncedCallback: debounceFiltersUpdate } = useDebounce(
    (newFilters: CalendarAssignmentsFiltersState) => {
      setDebouncedFilters(newFilters);
    },
    300
  );

  useEffect(() => {
    debounceFiltersUpdate(filters);
  }, [filters, debounceFiltersUpdate]);

  const onFilterChange = useCallback(
    (filter: FilterField, e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFilters((prev) => ({
        ...prev,
        [filter]: { ...prev[filter], value }
      }));
    },
    []
  );

  const onSortChange = useCallback(
    (field: 'name' | 'personnelNumber') => {
      setSortConfig((prev) => {
        if (prev.sortBy !== field) {
          return { sortBy: field, sortDirection: 'asc' };
        }

        const nextDirection =
          prev.sortDirection === 'asc'
            ? 'desc'
            : prev.sortDirection === 'desc'
              ? 'none'
              : 'asc';

        return nextDirection === 'none'
          ? { sortBy: 'none', sortDirection: 'none' }
          : { sortBy: field, sortDirection: nextDirection };
      });
    },
    [setSortConfig]
  );

  const handleSelectionChange = useCallback(
    (selection: TreeNode | null) => {
      const newFilters = {
        name: { value: '', matchMode: FilterMatchMode.CONTAINS },
        personnelNumber: { value: '', matchMode: FilterMatchMode.CONTAINS }
      } as CalendarAssignmentsFiltersState;

      setFilters(newFilters);
      setDebouncedFilters(newFilters); // Прямое обновление без задержки
      persistDepartmentFilter(selection);

      setSelectedDepartmentFilter(selection);

      if (!selection || selection.key === '') {
        toast.current?.show({
          severity: 'warn',
          detail: 'Фильтр участков сброшен'
        });
      }
    },
    [persistDepartmentFilter, setSelectedDepartmentFilter, toast]
  );

  const { debouncedCallback: debouncedHandleSelectionChange } = useDebounce(
    handleSelectionChange,
    DROPDOWN_FILTER_DELAY
  );

  const handleDateFilterChange = useCallback(
    (selection: Date | null) => {
      const isDifferent =
        (selection?.getTime() ?? null) !== (selectedDateFilter?.getTime() ?? null);

      if (isDifferent) {
        setSelectedDateFilter(selection);
        setTimelineScale(TimelineScale.MONTH);
      }
    },
    [selectedDateFilter, setSelectedDateFilter, setTimelineScale]
  );

  return {
    filters,
    debouncedFilters,
    onFilterChange,
    onSortChange,
    debouncedHandleSelectionChange,
    handleDateFilterChange,
    sortBy: sortConfig.sortBy,
    sortDirection: sortConfig.sortDirection
  };
}

